import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { getUnexpiredToken, useActiveProject } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { useAuthKit } from "@picahq/authkit";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { toast } from "sonner";

type AppCardProps = {
  icon: React.ReactNode;
  name: string;
  description: string;
  isInstalled?: boolean;
  connectionId?: string;
  refetchConnections: () => void;
  isFeatured?: boolean;
};

const AppCard = ({
  icon,
  name,
  description,
  isInstalled = false,
  connectionId,
  refetchConnections,
  isFeatured = false,
}: AppCardProps) => {
  const project = useActiveProject();
  const { session } = useSession();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const getSessionToken = async () => {
      // Only fetch token if not already set
      if (!token && session) {
        const newToken = await getUnexpiredToken(session);
        if (newToken) {
          setToken(newToken);
        }
      }
    };

    getSessionToken();
  }, [Boolean(session)]);

  // Use a ref to track if we've already processed this connection success
  const processedConnectionRef = useRef<string | null>(null);

  const kit = useAuthKit({
    token: {
      url: `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/auth-kit?project_id=${
        project?.id
      }`,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
    appTheme: "dark",
    selectedConnection: name,
    onSuccess: async (connection) => {
      // Check if the successful connection platform matches this card's name
      if (connection.platform !== name.toLowerCase()) {
        return; // Exit if the success event is not for this specific app card
      }

      // Create a unique connection identifier
      const connectionId = `${connection.platform}_${connection.key}`;

      // Check if we've already processed this connection
      if (processedConnectionRef.current === connectionId) {
        return;
      }

      // Set the ref to mark this connection as processed
      processedConnectionRef.current = connectionId;

      const connectionKeyName = `PICA_${connection.platform.toUpperCase()}_CONNECTION_KEY`;
      const connectionKey = connection.key;

      const secret = [
        {
          name: connectionKeyName,
          value: connectionKey,
        },
      ];

      try {
        // Get a fresh token directly from the session to ensure it's not expired
        const freshToken = await getUnexpiredToken(session);

        if (!freshToken) {
          console.error("No authentication token available");
          toast.error("Authentication error. Please try again.");
          return;
        }

        await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/edge-functions-secret`,
          {
            project_id: project.id,
            secret,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${freshToken}`,
            },
          }
        );
        toast.success(`Successfully connected ${name}`);
      } catch (error) {
        console.error(
          `Failed to upload Edge Function Secret for ${name}:`,
          error
        );
        toast.error(
          `Failed to connect ${name}: ${
            error.response?.status === 401
              ? "Authentication error"
              : "Server error"
          }`
        );
      }
    },
    onClose: () => {
      processedConnectionRef.current = null;
      refetchConnections();
    },
  });

  const [showUninstallDialog, setShowUninstallDialog] = useState(false);

  const handleUninstall = async () => {
    try {
      if (!connectionId) {
        toast.error("Failed to uninstall app: Missing connection ID");
        return;
      }
      const token = await getUnexpiredToken(session);
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/uninstall`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            connection_id: connectionId,
          }),
        }
      );

      const { success, error } = await response.json();
      if (success) {
        toast.success("App uninstalled successfully");
        // Call the refetch callback if provided
        refetchConnections();
      } else if (error) {
        toast.error("Failed to uninstall app");
      }
    } catch (error) {
      console.error("Failed to uninstall app:", error);
      toast.error("Failed to uninstall app");
    } finally {
      setShowUninstallDialog(false);
    }
  };

  const handleInstalledAppClick = () => {
    if (isInstalled) {
      setShowUninstallDialog(true);
    } else {
      // Try to open the install kit
      // Check if Supabase is connected
      const { connectedOrgId } = useSupabaseConnectStore.getState();
      const isSupabaseConnected = !!connectedOrgId;

      // Validate requirements
      if (!isSupabaseConnected) {
        toast.error("You must connect Supabase before using this app");
        return;
      }

      kit.open();
    }
  };

  const confirmUninstall = () => {
    handleUninstall();
    setShowUninstallDialog(false);
  };

  const cancelUninstall = () => {
    setShowUninstallDialog(false);
  };

  return (
    <>
      {/* Uninstall confirmation dialog */}
      <AlertDialog
        open={showUninstallDialog}
        onOpenChange={setShowUninstallDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Uninstall</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to uninstall {name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-4">
            <AlertDialogCancel onClick={cancelUninstall}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUninstall}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, uninstall
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div
        onClick={handleInstalledAppClick}
        className={`p-4 rounded-xl border border-secondary ${
          isInstalled ? "" : "hover:border-[#6448EF] cursor-pointer"
        } transition-colors mb-3`}
      >
        <Tooltip
          id="verified"
          place={"bottom"}
          className="z-[2000]"
          style={{
            fontSize: "0.75rem",
          }}
        />
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center justify-between w-full">
            <div className="w-6 h-6 flex items-center justify-center rounded-md relative">
              {icon}
              {/* Verified badge */}
              {isFeatured && (
                <>
                  <div
                    className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                    data-tooltip-id="verified-badge"
                    data-tooltip-content="This app is verified by Tempo"
                  >
                    <img src="/verified.svg" alt="Verified badge" />
                  </div>
                  <Tooltip id="verified-badge" />
                </>
              )}
            </div>
            {!isInstalled ? (
              <span className="p-1 px-2 border border-secondary text-xs bg-secondary rounded-md">
                Install
              </span>
            ) : (
              <span className="p-1 px-2 text-[#606064] font-medium text-xs">
                Installed
              </span>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-white text-md font-medium">{name}</h3>
            <p className="text-[#8E8E8E] text-sm mb-4 flex-grow line-clamp-2 overflow-hidden">
              {description}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppCard;
